// The world's borders, as a question one point at a time.
//
// The ranking map paints a field of cells and each cell has to answer "WHICH country is
// this place in" before it can be scored. Not "is it in the one being looked at": the
// public page ranks every listing for every visitor and country is only the hundreds
// column of the score, so a storefront places somewhere for a visitor in the next country
// too, and the map has to be able to paint that. The only ground with no answer is water,
// where no visitor can stand, and that is the only ground left unpainted.
//
// That is thousands of questions per repaint, asked again on every pan, zoom and
// keystroke, which is why the answering is here rather than in the map: no DOM, no
// Google, no state, so it can be tested in node against the real border file the browser
// downloads.
//
// The file is app/assets/data/world_borders.geojson. Its features carry exactly one
// property, iso2, and the ranking compares countries by that code alone - the same
// uppercase ISO code MaxMind hands the public page and the form's country select stores.
//
// Two facts about that file drive the whole shape of this:
//
//   A country is not one outline. Twenty-nine of the hundred and seventy-five are
//   MultiPolygons - Fiji is islands, Russia is fourteen pieces - so membership is "inside
//   ANY of its polygons", never "inside its polygon".
//
//   A polygon is not one ring. South Africa's outline has a second ring cut out of it,
//   and that hole is Lesotho. A test that stopped at the outer ring would put Maseru in
//   two countries at once and score a Johannesburg branch as local to it.
//
// A hundred and seventy-five is not every country, and that is a decision rather than an
// oversight. This is Natural Earth's 110m set, which drops the smallest states - SG, MC,
// LI, AD, MT and BH among them - and the storefront country select offers codes for some
// of those. Techflex will not have a dealer in any of them, confirmed with the maintainers
// 2026-09-04, so the coverage this file is short of is coverage nothing will ask it for.
//
// What it would cost to close anyway is the reason not to: a finer border set is several
// times this one's 244 KB, downloaded by every visitor to the public page, and walked
// again per cell on every pan, zoom and keystroke. That is a real price on every ranking
// for six markets that will stay empty.
//
// A code with no outline is not an error here. It paints nothing over that country and
// #fit falls back to the world view instead of framing it - see the "a country the file
// does not hold is nowhere" test. The RANKING is unaffected either way: #positionFrom
// compares listing.country as a string and reads no geometry at all, so a listing in a
// country this file has never heard of still places correctly.
//
// Ray casting rather than anything cleverer: the even-odd rule needs no preprocessing
// beyond the bounding boxes below, and the boxes are what make it cheap. A cell is
// rejected against the country's box, then against the box of each polygon, before any
// ring is walked at all - which for a country-sized viewport is nearly every cell.
//
// Longitude is x and latitude is y throughout, because that is GeoJSON's own order and
// flipping it here would mean flipping it back at every call site.

/**
 * Every country in a border collection, keyed by its ISO code, with the boxes the
 * membership test needs precomputed.
 *
 * A geometry that is neither Polygon nor MultiPolygon is skipped rather than guessed at.
 * The file carries only those two, and a country read wrongly would answer questions
 * about the wrong ground - worse than a country that answers none.
 *
 * @param {{features: Array<object>}} collection parsed GeoJSON FeatureCollection
 * @returns {Map<string, object>} iso2 to a country the functions below accept
 */
export function indexCountries(collection) {
  const index = new Map();

  for (const feature of collection.features || []) {
    const iso2 = feature.properties && feature.properties.iso2;
    const geometry = feature.geometry || {};
    if (!iso2) continue;

    let outlines;
    if (geometry.type === "Polygon") outlines = [geometry.coordinates];
    else if (geometry.type === "MultiPolygon") outlines = geometry.coordinates;
    else continue;

    const polygons = outlines.map(polygonOf);
    const boxes = polygons.map((one) => one.bbox);
    index.set(iso2, { iso2, polygons, bbox: enclosing(boxes), view: viewOf(boxes) });
  }

  return index;
}

/**
 * Whether a point stands in a country.
 *
 * @param {number} lat degrees
 * @param {number} lng degrees
 * @param {object|null|undefined} country an entry from indexCountries
 * @returns {boolean} false for a country the index does not hold, so a listing filed
 *   under a code the border file has never heard of simply never matches
 */
export function pointInCountry(lat, lng, country) {
  if (!country || !inBox(lat, lng, country.bbox)) return false;

  return country.polygons.some(
    (polygon) =>
      inBox(lat, lng, polygon.bbox) &&
      inRing(lat, lng, polygon.outer) &&
      !polygon.holes.some((hole) => inRing(lat, lng, hole)),
  );
}

/**
 * The country a point stands in, or null for open water.
 *
 * Linear over the candidates, and deliberately so. A grid or an R-tree would beat this
 * asymptotically, but the whole file is 175 countries in 286 polygons and 10,612 vertices,
 * and #countriesOverlapping has already cut the candidates to the handful the viewport
 * touches - so the scan is a few bounding-box comparisons and at most one or two rings
 * walked. An index would cost build time and a second description of the same geometry to
 * save nothing measurable.
 *
 * The first match wins. The border file's polygons do not overlap, so there is at most one
 * answer; a point on a shared border resolves to whichever country the file lists first,
 * which is a pixel's worth of arbitrariness on a cell six pixels wide.
 *
 * @param {number} lat degrees
 * @param {number} lng degrees
 * @param {Array<object>} candidates entries from indexCountries - an ARRAY, not the Map,
 *   because this is called once per cell and re-deriving an iterator per call is the kind
 *   of allocation that shows up on a pass this size
 * @returns {object|null} the country, or null when the point is in no country at all
 */
export function countryAt(lat, lng, candidates) {
  for (let index = 0; index < candidates.length; index += 1) {
    if (pointInCountry(lat, lng, candidates[index])) return candidates[index];
  }

  return null;
}

/**
 * The countries whose ground could appear inside a box, which for a map-sized viewport is
 * a few out of the hundred and seventy-five.
 *
 * A box test only, so the answer is a superset - a country whose bounding box clips the
 * viewport while its coastline does not is still returned, and countryAt then rejects
 * every cell against it. That is the right way round: cutting on the box is what makes
 * the per-cell scan short, and cutting on anything finer would be doing the per-cell work
 * twice.
 *
 * @param {{south: number, west: number, north: number, east: number}} box
 * @param {Map<string, object>|Iterable<object>} countries as indexCountries returns them
 * @returns {Array<object>} candidates for countryAt
 */
export function countriesOverlapping(box, countries) {
  const values = countries instanceof Map ? countries.values() : countries;

  return [...values].filter((country) => overlaps(box, country.bbox));
}

function overlaps(one, other) {
  return (
    one.south <= other.north &&
    one.north >= other.south &&
    one.west <= other.east &&
    one.east >= other.west
  );
}

// GeoJSON's ring order is the contract: the first is the outline, every ring after it is
// a hole punched in it.
function polygonOf(rings) {
  const [outer, ...holes] = rings;

  return { outer, holes, bbox: boxOf(outer) };
}

function boxOf(ring) {
  const box = { south: Infinity, west: Infinity, north: -Infinity, east: -Infinity };

  for (const [lng, lat] of ring) {
    if (lat < box.south) box.south = lat;
    if (lat > box.north) box.north = lat;
    if (lng < box.west) box.west = lng;
    if (lng > box.east) box.east = lng;
  }

  return box;
}

function enclosing(boxes) {
  return {
    south: Math.min(...boxes.map((box) => box.south)),
    west: Math.min(...boxes.map((box) => box.west)),
    north: Math.max(...boxes.map((box) => box.north)),
    east: Math.max(...boxes.map((box) => box.east)),
  };
}

// The frame to OPEN on a country, which is not always the box that encloses it.
//
// #bbox is a superset by construction and that is what makes it safe to prune with: a
// point outside it is outside the country. It is the wrong thing to look at, though. A
// country either side of the antimeridian has vertices at both -180 and 180, so the
// minimum and the maximum are the ends of the world and the box is the whole of it -
// Russia and Fiji are both that shape here, and RankingMapController#fit opened their
// dealers on the globe instead of on their own market.
//
// So the longitudes are treated as what they are, arcs on a circle: merge the polygons'
// spans, find the widest gap between them, and keep everything except the gap. Russia's
// widest gap is the Atlantic, and what is left is Kaliningrad round to Big Diomede - 170
// degrees rather than 360. It comes back with west greater than east when it wraps, which
// is how Google Maps reads a bounds that crosses the line, and #bbox is untouched so
// nothing that prunes has to learn any of this.
//
// Antarctica really does encircle the pole and really does want the whole world, and gets
// it: its spans leave no gap worth the name.
function viewOf(boxes) {
  const spans = boxes
    .map((box) => ({ west: box.west, east: box.east }))
    .sort((one, other) => one.west - other.west);

  const merged = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span.west <= last.east) last.east = Math.max(last.east, span.east);
    else merged.push({ ...span });
  }

  if (merged.length === 1) return merged[0];

  let widest = -1;
  let after = 0;
  for (let index = 0; index < merged.length; index += 1) {
    const next = merged[(index + 1) % merged.length];
    const gap =
      index === merged.length - 1 ? next.west + 360 - merged[index].east : next.west - merged[index].east;

    if (gap > widest) {
      widest = gap;
      after = index;
    }
  }

  return { west: merged[(after + 1) % merged.length].west, east: merged[after].east };
}

function inBox(lat, lng, box) {
  return lat >= box.south && lat <= box.north && lng >= box.west && lng <= box.east;
}

// The even-odd rule: count the edges a ray cast east from the point crosses, and an odd
// count means inside. The pair is read as (previous, current) so the closing edge back to
// the first vertex is walked like any other - GeoJSON repeats the first point as the last,
// and a ring left open would leak.
//
// An edge whose two ends sit on the same side of the point's latitude cannot be crossed,
// which is also what keeps the repeated closing vertex from counting twice and what keeps
// the division below off a zero denominator.
//
// The vertices are read by index rather than destructured, which is not a style choice.
// `const [lng, lat] = ring[i]` is array destructuring, and V8 compiles that to the ITERATOR
// protocol - an allocation and a pair of .next() calls per vertex - because it cannot prove
// the element is a fast-elements array. Measured over the largest US ring, that is 17.7
// microseconds a call against 933 nanoseconds for the indexed form: nineteen times, paid
// once per country per cell, on a pass that asks this thousands of times per repaint.
function inRing(lat, lng, ring) {
  let inside = false;

  for (let current = 0, previous = ring.length - 1; current < ring.length; previous = current++) {
    const here = ring[current];
    const there = ring[previous];
    const lngC = here[0];
    const latC = here[1];
    const lngP = there[0];
    const latP = there[1];

    if (latC > lat !== latP > lat && lng < ((lngP - lngC) * (lat - latC)) / (latP - latC) + lngC) {
      inside = !inside;
    }
  }

  return inside;
}
